<template>
  <section
    class="gs-app gm-studio"
    style="padding:0;min-height:0;background:var(--gs-surface)"
  >
    <div class="gm-toolbar gm-editor-mode-toolbar">
      <gs-button
        v-for="tab in availableModes"
        :key="tab"
        variant="outline"
        :aria-pressed="mode === tab"
        @click="changeMode(tab)"
      >{{ tab }}</gs-button>
      <gs-button variant="outline" @click="gallery = true">Choose a template</gs-button>
      <select
        v-model="variable"
        aria-label="Insert a merge variable"
        @change="insertVariable"
      >
        <option value="">Insert a merge variable</option>
        <option
          v-for="item in variableOptions"
          :key="item.value"
          :value="item.value"
        >{{ item.label }}</option>
      </select>
    </div>

    <p v-if="error" class="gm-error" role="alert">
      {{ error }}
      <gs-button v-if="!policyReady" variant="outline" @click="loadPolicy">Retry link checks</gs-button>
    </p>
    <p v-if="!policyReady && !error" role="status">Loading link restrictions…</p>

    <div v-if="mode === 'Basic' && editor" class="gm-rich-editor">
      <div class="gm-toolbar gm-rich-toolbar" aria-label="Rich text formatting">
        <gs-button
          v-for="format in formats"
          :key="format.command"
          size="icon"
          variant="ghost"
          :title="format.label"
          :aria-label="format.label"
          @click="run(format.command, format.attrs)"
        ><gs-icon :name="format.icon" size="18" /></gs-button>
        <gs-button
          size="icon"
          variant="ghost"
          title="Insert link"
          aria-label="Insert link"
          @click="linkDialog = true"
        ><gs-icon name="link" size="18" /></gs-button>
        <gs-button
          size="icon"
          variant="ghost"
          title="Insert image"
          aria-label="Insert image"
          @click="handleImageInsertionInContent"
        ><gs-icon name="image" size="18" /></gs-button>
        <label class="gm-toolbar-select" title="Heading level">
          <gs-icon name="heading" size="16" aria-hidden="true" />
          <select aria-label="Heading level" @change="setHeading($event.target.value)">
            <option value="0">Normal</option>
            <option v-for="level in 6" :key="level" :value="level">H{{ level }}</option>
          </select>
        </label>
        <label class="gm-toolbar-select" title="Font size">
          <span class="gm-toolbar-letter" aria-hidden="true">A</span>
          <select aria-label="Font size" @change="setFontSize($event.target.value)">
            <option value="">Normal</option>
            <option value="10px">Small</option>
            <option value="18px">Large</option>
            <option value="32px">Huge</option>
          </select>
        </label>
        <label class="gm-color-control" title="Text color">
          <span class="gm-toolbar-letter" aria-hidden="true">A</span>
          <input v-model="textColor" type="color" aria-label="Text color" @change="setTextColor" />
        </label>
        <label class="gm-color-control" title="Text background color">
          <span class="gm-toolbar-highlight" aria-hidden="true">A</span>
          <input v-model="backgroundColor" type="color" aria-label="Text background color" @change="setBackgroundColor" />
        </label>
        <label class="gm-toolbar-select" title="Text alignment">
          <gs-icon name="align-left" size="16" aria-hidden="true" />
          <select aria-label="Text alignment" @change="setAlignment($event.target.value)">
            <option value="">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
            <option value="justify">Justify</option>
          </select>
        </label>
        <gs-button size="icon" variant="ghost" title="Clear formatting" aria-label="Clear formatting" @click="clearInlineStyle">
          <gs-icon name="eraser" size="18" />
        </gs-button>
        <gs-button size="icon" variant="ghost" title="Undo" aria-label="Undo" @click="run('undo')">
          <gs-icon name="undo" size="18" />
        </gs-button>
        <gs-button size="icon" variant="ghost" title="Redo" aria-label="Redo" @click="run('redo')">
          <gs-icon name="redo" size="18" />
        </gs-button>
      </div>
      <editor-content :editor="editor" />
    </div>

    <html-source v-if="mode === 'HTML'" :value="content" @input="update" />

    <div v-if="mode === 'Preview'">
      <div class="gm-toolbar">
        <gs-button variant="outline" @click="mobile = !mobile">
          {{ mobile ? 'Desktop preview' : 'Mobile preview' }}
        </gs-button>
      </div>
      <iframe
        class="gm-preview-frame"
        :class="{ 'gm-preview-mobile': mobile }"
        :srcdoc="preview(previewHtml)"
        sandbox=""
        title="Message preview"
      />
    </div>

    <small v-if="mode === 'HTML'">
      HTML is preserved as written. Preview is isolated; scripts and forms cannot run. Changes with invalid links are not applied.
    </small>

    <gs-modal v-model="gallery" title="Choose an email template" subtitle="Professional designs and your saved templates." size="xl">
      <email-gallery embedded @choose="selectTemplate" />
    </gs-modal>

    <gs-modal v-model="designOpen" title="Customize your email" size="xl">
      <email-document-editor
        v-if="chosen"
        :key="chosen.id"
        :template="chosen"
        @change="designResult = $event"
      />
      <div class="gm-panel gm-stack" style="margin-top:20px">
        <label class="gm-field">
          Your company name
          <input v-model="companyName" placeholder="Used wherever the template says company_name" />
        </label>
        <div v-if="templateError" class="gm-error" role="alert">{{ templateError }}</div>
        <label v-if="chosen && (chosen.subject || chosen.previewText)" class="gm-check">
          <input v-model="includeMetadata" type="checkbox" />Use the template subject and preview text
        </label>
        <div class="gm-actions">
          <gs-button variant="outline" @click="designOpen = false">Keep current message</gs-button>
          <gs-button @click="applyTemplate">Use this email</gs-button>
        </div>
        <small>This replaces the current message body. Review the subject and preview text in your mailing setup before sending.</small>
      </div>
    </gs-modal>

    <gs-modal v-model="convertConfirm" title="Switch to the basic editor?">
      <p>The basic editor supports text, links, lists and images. Email tables and custom HTML styling may be simplified. Keep HTML mode to retain this design exactly.</p>
      <div class="gm-actions">
        <gs-button variant="outline" @click="convertConfirm = false">Keep HTML</gs-button>
        <gs-button @click="convertToBasic">Switch to basic</gs-button>
      </div>
    </gs-modal>

    <gs-modal v-model="linkDialog" title="Insert a link">
      <label class="gm-field">Link URL
        <input v-model="linkUrl" type="url" placeholder="https://example.com" />
      </label>
      <div class="gm-actions" style="margin-top:16px">
        <gs-button variant="outline" @click="removeLink">Remove link</gs-button>
        <gs-button @click="insertLink">Insert link</gs-button>
      </div>
    </gs-modal>

    <gs-modal v-model="imageDialog" title="Insert an image" size="xl">
      <div class="gm-stack">
        <groove-image-library
          :value="imagePickerVal"
          modal="true"
          remove="false"
          @close-modal="imageDialog = false"
          @selected="insertImageFromPicker"
        />
        <div class="gm-divider">Or use an image URL</div>
        <label class="gm-field">Image URL
          <input v-model="imageUrl" type="url" placeholder="https://example.com/image.jpg" />
        </label>
        <label class="gm-field">Alternative text
          <input v-model="imageAlt" placeholder="Describe the image" />
        </label>
        <gs-button @click="insertImage">Insert image</gs-button>
      </div>
    </gs-modal>

    <gs-modal v-model="affiliateDialog" title="Insert affiliate link">
      <div class="gm-stack">
        <label class="gm-field">Promotion
          <select v-model="affiliatePromo" @change="loadAffiliateLinks">
            <option value="">Select a promotion</option>
            <option v-for="promo in promos" :key="promo.id" :value="promo.id">{{ promo.name }}</option>
          </select>
        </label>
        <label class="gm-field">Affiliate link
          <select v-model="affiliateLink">
            <option value="">Select an affiliate link</option>
            <option v-for="link in affiliateLinks" :key="link.id" :value="link.id">{{ link.name }}</option>
          </select>
        </label>
        <p v-if="affiliateError" class="gm-error" role="alert">{{ affiliateError }}</p>
        <div class="gm-actions">
          <gs-button variant="outline" @click="affiliateDialog = false">Cancel</gs-button>
          <gs-button @click="insertAffiliateLink">Insert link</gs-button>
        </div>
      </div>
    </gs-modal>
  </section>
</template>

<script>
import './index';
import axios from 'axios';
import { Editor, EditorContent } from 'tiptap';
import {
  Bold,
  Italic,
  Strike,
  Underline,
  BulletList,
  OrderedList,
  ListItem,
  Blockquote,
  Link,
  Image,
  History,
  Placeholder
} from 'tiptap-extensions';
import HtmlSource from './HtmlSource.vue';
import EmailGallery from './EmailGallery.vue';
import EmailDocumentEditor from './EmailDocumentEditor.vue';
// Reuse the same unified GroovePages library so GrooveMail can browse Groove,
// PageBuilder, Imager, CreatorStudio, stock, and AI-created images.
import GrooveImageLibrary from '../../../../gp-ui/src/builder/ui/media/ImageLibrary.vue';
import {
  AlignableParagraph,
  AlignableHeading,
  EmailHardBreak,
  StyledText,
  TextAlign,
  legacyHtmlNeedsSourceMode
} from './rich-text-extensions';
import { serializeStandardEmailHtml } from './emailHtmlSerialization';
import { plainTextFromHtml } from '../../components/emailContentValidation';
import {
  grooveEmail,
  validateEmailHtml,
  errorMessage,
  previewDocument,
  safeUrl,
  clone
} from './contracts';

export default {
  components: {
    EditorContent,
    HtmlSource,
    EmailGallery,
    EmailDocumentEditor,
    GrooveImageLibrary
  },
  props: {
    value: { type: String, default: '' },
    htmlMode: Boolean,
    restricted: Boolean,
    dynamicVariables: { default: () => [] },
    allowPreview: Boolean,
    allowHtml: Boolean,
    openedFrom: { type: String, default: 'campaign' },
    editorNodeWithNoMargin: Boolean
  },
  data() {
    return {
      content: this.value || '',
      mode: this.htmlMode || legacyHtmlNeedsSourceMode(this.value) ? 'HTML' : 'Basic',
      editor: null,
      error: '',
      policyReady: !this.restricted,
      blocked: [],
      gallery: false,
      designOpen: false,
      chosen: null,
      designResult: null,
      companyName: '',
      includeMetadata: true,
      templateError: '',
      mobile: false,
      convertConfirm: false,
      linkDialog: false,
      imageDialog: false,
      imagePickerVal: '',
      linkUrl: '',
      imageUrl: '',
      imageAlt: '',
      variable: '',
      affiliateDialog: false,
      affiliatePromo: '',
      affiliateLink: '',
      affiliateError: '',
      promos: [],
      affiliateLinks: [],
      unsubscribeContent: '',
      textColor: '#222222',
      backgroundColor: '#ffff00',
      formats: [
        { command: 'bold', label: 'Bold', icon: 'bold' },
        { command: 'italic', label: 'Italic', icon: 'italic' },
        { command: 'underline', label: 'Underline', icon: 'underline' },
        { command: 'strike', label: 'Strike', icon: 'strikethrough' },
        { command: 'bullet_list', label: 'Bullet list', icon: 'list-ul' },
        { command: 'ordered_list', label: 'Numbered list', icon: 'list-ol' },
        { command: 'blockquote', label: 'Quote', icon: 'quote' }
      ]
    };
  },
  computed: {
    availableModes() {
      // Source mode is intentionally always available so users can inspect
      // and repair the exact email HTML, including legacy campaigns.
      return ['Basic', 'HTML', 'Preview'];
    },
    variableOptions() {
      const values = [];
      const add = (value, label) => {
        if (!value || !/^\{[^{}]+\}$/.test(String(value)) || values.some(item => item.value === value)) return;
        values.push({ value: String(value), label: label || String(value) });
      };
      const walk = input => {
        if (Array.isArray(input)) input.forEach(walk);
        else if (input && typeof input === 'object') {
          if (input.value) add(input.value, input.label || input.name);
          else Object.values(input).forEach(walk);
        } else if (typeof input === 'string') add(input);
      };
      walk(this.dynamicVariables);
      [
        ['{first_name}', 'First name'],
        ['{last_name}', 'Last name'],
        ['{email}', 'Email'],
        ['{phone_number}', 'Phone number'],
        ['{optin_confirmation_link}', 'Confirmation link']
      ].forEach(([value, label]) => add(value, label));
      return values;
    },
    previewHtml() {
      const footer = this.openedFrom !== 'admin' && this.unsubscribeContent
        ? `<p style="margin:0"><br></p>${this.unsubscribeContent}`
        : '';
      return this.content + footer;
    },
    genericUrl() {
      const apiUrl = window.config && window.config.apiUrl ? window.config.apiUrl : '';
      return apiUrl.substr(0, apiUrl.lastIndexOf('/'));
    }
  },
  watch: {
    value(value) {
      const next = value || '';
      if (next === this.content) return;
      this.content = next;
      if (this.editor && this.mode === 'Basic' && this.editor.getHTML() !== next) {
        this.editor.setContent(next, false);
      }
      if (legacyHtmlNeedsSourceMode(next)) this.mode = 'HTML';
    },
    htmlMode(value) {
      if (value) this.mode = 'HTML';
      else if (this.mode === 'HTML') this.changeMode('Basic');
    }
  },
  mounted() {
    this.editor = new Editor({
      extensions: [
        new AlignableParagraph({ noMargin: this.editorNodeWithNoMargin }),
        new AlignableHeading({ levels: [1, 2, 3, 4, 5, 6], noMargin: this.editorNodeWithNoMargin }),
        new Bold(),
        new Italic(),
        new Strike(),
        new Underline(),
        new StyledText(),
        new BulletList(),
        new OrderedList(),
        new ListItem(),
        new Blockquote(),
        new EmailHardBreak(),
        new Link({ openOnClick: false }),
        new Image(),
        new History(),
        new TextAlign(),
        new Placeholder({ emptyNodeText: 'Write your email here…' })
      ],
      content: this.mode === 'Basic' ? this.content : '',
      onUpdate: () => {
        if (this.mode !== 'Basic') return;
        const html = this.getHtml();
        if (html != null) this.update(html);
      }
    });
    if (this.mode === 'Basic') {
      const html = this.getHtml();
      if (typeof html === 'string') this.content = html;
    }
    this.loadPolicy();
    this.fetchUnsubscribeContent();
  },
  beforeDestroy() {
    if (this.editor) this.editor.destroy();
  },
  methods: {
    preview: previewDocument,
    // Basic mode reads the live TipTap document. HTML mode keeps the source
    // the user is editing, which is not loaded into the TipTap instance.
    getPlainText() {
      try {
        if (this.mode !== 'Basic') return plainTextFromHtml(this.content);
        const html = this.getHtml();
        if (html == null) return null;
        return plainTextFromHtml(html);
      } catch (error) {
        return null;
      }
    },
    getHtml() {
      try {
        if (this.mode !== 'Basic') return this.content || '';
        const raw = this.editor && typeof this.editor.getHTML === 'function'
          ? this.editor.getHTML()
          : this.content;
        return serializeStandardEmailHtml(raw || '');
      } catch (error) {
        return null;
      }
    },
    run(command, attrs) {
      if (!this.editor || !this.editor.commands[command]) return;
      this.editor.commands[command](attrs);
    },
    async loadPolicy() {
      if (!this.restricted) {
        this.policyReady = true;
        return;
      }
      try {
        const response = await axios.get('/get-blocked-domains-and-messages');
        if (!Array.isArray(response.data)) throw new Error('Unable to load link restrictions.');
        this.blocked = response.data;
        this.policyReady = true;
        this.error = '';
      } catch (error) {
        this.error = errorMessage(error) + ' Changes are not applied until link checks are available.';
        this.policyReady = false;
      }
    },
    async fetchUnsubscribeContent() {
      if (this.openedFrom === 'admin') return;
      try {
        const response = await axios.get('/setting/get-unsubscribe-layout');
        this.unsubscribeContent = response.data && response.data.footer_layout
          ? response.data.footer_layout
          : '';
      } catch (error) {
        this.unsubscribeContent = '';
      }
    },
    check(html) {
      if (!this.policyReady) throw new Error('Link checks are unavailable. Changes have not been applied.');
      validateEmailHtml(html);
      const blocked = this.blocked.find(item =>
        item.domain && html.toLowerCase().includes(String(item.domain).toLowerCase())
      );
      if (blocked) throw new Error((blocked.message || 'This link is not allowed.') + ' Remove it to apply your changes.');
    },
    update(html) {
      this.content = html;
      try {
        this.check(html);
        this.error = '';
        this.$emit('input', html);
      } catch (error) {
        this.error = error.message;
      }
    },
    changeMode(nextMode) {
      if (nextMode === this.mode) return;
      if (nextMode === 'Basic') {
        if (legacyHtmlNeedsSourceMode(this.content)) {
          this.convertConfirm = true;
          return;
        }
        if (this.editor) this.editor.setContent(this.content || '<p></p>', false);
      }
      this.mode = nextMode;
      if (nextMode === 'Basic') {
        const html = this.getHtml();
        if (typeof html === 'string') this.content = html;
      }
    },
    convertToBasic() {
      this.convertConfirm = false;
      if (this.editor) this.editor.setContent(this.content || '<p></p>', false);
      this.mode = 'Basic';
      const html = this.getHtml();
      if (html != null) this.update(html);
    },
    selectTemplate(template) {
      this.chosen = template;
      this.includeMetadata = !!(template.subject || template.previewText);
      this.designResult = clone(template);
      this.gallery = false;
      this.templateError = '';
      this.designOpen = true;
    },
    applyTemplate() {
      try {
        const html = grooveEmail(this.designResult.bodyHtml, this.companyName);
        const plain = value => new DOMParser().parseFromString(
          grooveEmail(String(value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'), this.companyName),
          'text/html'
        ).body.textContent;
        const metadata = this.includeMetadata ? {
          subject: plain(this.chosen.subject),
          previewText: plain(this.chosen.previewText)
        } : null;
        this.check(html);
        this.mode = 'HTML';
        this.update(html);
        if (metadata) this.$emit('template-selected', metadata);
        this.designOpen = false;
      } catch (error) {
        this.templateError = error.message;
      }
    },
    insertVariable() {
      if (!this.variable) return;
      if (this.variable === '{affilate_link}' || this.variable === '{affiliate_link}') {
        this.openAffiliateLinkModal();
      } else if (this.mode === 'Basic' && this.editor) {
        if (this.editor.commands.insertText) this.editor.commands.insertText(this.variable);
        else this.editor.view.dispatch(this.editor.state.tr.insertText(this.variable));
      } else {
        this.mode = 'HTML';
        this.update(this.content + this.variable);
      }
      this.variable = '';
    },
    async openAffiliateLinkModal() {
      this.affiliateError = '';
      this.affiliatePromo = '';
      this.affiliateLink = '';
      this.affiliateLinks = [];
      this.affiliateDialog = true;
      try {
        await this.fetchPromos();
      } catch (error) {
        this.affiliateError = errorMessage(error);
      }
    },
    async fetchPromos() {
      const response = await axios.get(`${this.genericUrl}/groovesell/promos`);
      this.promos = response.data && response.data.data ? response.data.data : [];
    },
    async loadAffiliateLinks() {
      this.affiliateLink = '';
      this.affiliateLinks = [];
      if (!this.affiliatePromo) return;
      try {
        const response = await axios.get(`${this.genericUrl}/groovesell/affiliate-links?funnel_id=${encodeURIComponent(this.affiliatePromo)}`);
        this.affiliateLinks = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        this.affiliateError = errorMessage(error);
      }
    },
    insertAffiliateLink() {
      const selected = this.affiliateLinks.find(item => String(item.id) === String(this.affiliateLink));
      if (!selected || !selected.link) {
        this.affiliateError = 'Select an affiliate link.';
        return;
      }
      if (this.mode === 'Basic' && this.editor) {
        if (this.editor.commands.insertText) this.editor.commands.insertText(selected.link);
        else this.editor.view.dispatch(this.editor.state.tr.insertText(selected.link));
      } else {
        this.mode = 'HTML';
        this.update(this.content + selected.link);
      }
      this.affiliateDialog = false;
    },
    setHeading(value) {
      const level = Number(value);
      if (!this.editor) return;
      if (level) this.run('toggleHeading', { level });
      else if (this.editor.commands.paragraph) this.run('paragraph', {});
    },
    setFontSize(value) {
      this.run('setInlineStyle', { fontSize: value || null });
    },
    setTextColor() {
      this.run('setInlineStyle', { color: this.textColor });
    },
    setBackgroundColor() {
      this.run('setInlineStyle', { backgroundColor: this.backgroundColor });
    },
    setAlignment(value) {
      this.run('setTextAlign', { textAlign: value || null });
    },
    clearInlineStyle() {
      this.run('setInlineStyle', { color: null, backgroundColor: null, fontSize: null, fontFamily: null });
    },
    insertLink() {
      if (!safeUrl(this.linkUrl)) {
        this.error = 'Enter a valid link URL.';
        return;
      }
      const blocked = this.blocked.find(item =>
        item.domain && this.linkUrl.toLowerCase().includes(String(item.domain).toLowerCase())
      );
      if (blocked) {
        this.error = (blocked.message || 'This link is not allowed.') + ' Remove it to apply your changes.';
        return;
      }
      this.run('link', { href: this.linkUrl });
      this.linkDialog = false;
      this.linkUrl = '';
    },
    removeLink() {
      this.run('link', { href: null });
      this.linkDialog = false;
      this.linkUrl = '';
    },
    handleImageInsertionInContent() {
      this.imageDialog = true;
      return true;
    },
    insertImageFromPicker(image) {
      const url = typeof image === 'string' ? image : image && (image.url || image.src);
      if (!url) return;
      this.imageUrl = String(url).replace(/ /g, '%20');
      this.insertImage();
    },
    insertImage() {
      if (!/^https?:\/\//i.test(this.imageUrl)) {
        this.error = 'Use an HTTP or HTTPS image URL.';
        return;
      }
      const blocked = this.blocked.find(item =>
        item.domain && this.imageUrl.toLowerCase().includes(String(item.domain).toLowerCase())
      );
      if (blocked) {
        this.error = (blocked.message || 'This image URL is not allowed.') + ' Remove it to apply your changes.';
        return;
      }
      this.run('image', { src: this.imageUrl, alt: this.imageAlt || null });
      this.imageDialog = false;
      this.imageUrl = '';
      this.imageAlt = '';
      this.imagePickerVal = '';
    }
  }
};
</script>
